import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../Context/CartContext';
import { useLanguage, getLanguageText } from '../Context/LanguageContext';
import { useWishlist } from '../Context/WishlistContext';
import { getProducts, getCategories, getActiveDiscounts } from '../firebase/services';
import ProductCard from '../Components/ProductCard';
import LoadingScreen from '../Components/LoadingScreen';
import SEO from '../Components/SEO';
import { getBestDiscountForProduct, computeDiscountedPrice, formatDiscountBadge } from '../Utils/discounts';

export default function ProductDetails() {
  // eslint-disable-next-line no-unused-vars
  const { categoryName, productSlug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { language } = useLanguage();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedTaste, setSelectedTaste] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [matchedDiscount, setMatchedDiscount] = useState(null);

  const toSlug = React.useCallback((value, fallback = 'uncategorized') => {
    const raw = (value ?? '').toString();
    const firstPart = raw.split(' / ')[0].trim().toLowerCase();
    const cleaned = firstPart
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\u0600-\u06FF-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    return cleaned || fallback;
  }, []);

  const getExpectedCategorySlug = React.useCallback((p, allCats) => {
    if (!p) return 'uncategorized';
    const rawPrimary = (p?.categories && Array.isArray(p.categories) && p.categories.length > 0)
      ? p.categories[0]
      : (p?.categoryId || p?.category);

    const resolved = (allCats || []).find(c => c?.id === rawPrimary) || null;
    const label = resolved
      ? (resolved.nameEn || resolved.name || resolved.nameAr)
      : (typeof rawPrimary === 'string' ? rawPrimary : 'uncategorized');

    return toSlug(label, 'uncategorized');
  }, [toSlug]);

  const resolveCategoryLabel = React.useCallback((catIdOrName) => {
    if (!catIdOrName) return '';
    const matched = categories.find(c => c.id === catIdOrName) || null;
    if (!matched) return getLanguageText(catIdOrName, language);

    const bilingualName = matched.name
      ?? ((matched.nameEn || matched.nameAr)
        ? { en: matched.nameEn || matched.name || '', ar: matched.nameAr || matched.name || '' }
        : '');

    return getLanguageText(bilingualName, language) || matched.name || '';
  }, [categories, language]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const [allProducts, allCategories] = await Promise.all([
          getProducts(),
          getCategories()
        ]);
        setCategories(allCategories || []);
        // Find product by slug (name)
        const foundProduct = allProducts.find(p => {
          let name = '';
          if (p.nameEn) {
            name = p.nameEn;
          } else if (typeof p.name === 'string') {
            name = p.name;
          } else if (typeof p.name === 'object' && p.name !== null) {
            name = p.name.en || p.name.ar || '';
          }
          // Take the first part before "/" if bilingual name
          const firstPart = name.split(' / ')[0];
          const slug = firstPart.toLowerCase().replace(/\s+/g, '-');
          return slug === productSlug;
        });
        setProduct(foundProduct);

        // If the URL category segment is an old category ID (or just wrong),
        // redirect to the resolved category slug. Keeps old links working and improves SEO.
        if (foundProduct) {
          const expectedCategorySlug = getExpectedCategorySlug(foundProduct, allCategories || []);
          if (expectedCategorySlug && categoryName !== expectedCategorySlug) {
            navigate(`/products/${expectedCategorySlug}/${productSlug}`, { replace: true });
          }
        }
        
        // Find related products
        if (foundProduct) {
          const currentTags = foundProduct.tags || [];
          const currentCategories = foundProduct.categories || (foundProduct.category ? [foundProduct.category] : []);
          
          const related = allProducts
            .filter(p => p.id !== foundProduct.id)
            .map(p => {
              let score = 0;
              const pTags = p.tags || [];
              const pCategories = p.categories || (p.category ? [p.category] : []);
              
              // Match tags (higher weight)
              if (currentTags.length > 0) {
                const sharedTags = pTags.filter(t => currentTags.includes(t));
                score += sharedTags.length * 2;
              }
              
              // Match categories (lower weight)
              const sharedCats = pCategories.filter(c => currentCategories.includes(c));
              score += sharedCats.length;
              
              return { ...p, score };
            })
            .filter(p => p.score > 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, 4);
            
          setRelatedProducts(related);
        }

        // Set default selected taste if available
        if (foundProduct && foundProduct.tastes && foundProduct.tastes.length > 0) {
          setSelectedTaste(0);
        }
      } catch (error) {
        console.error("Failed to fetch product", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productSlug, categoryName, navigate, getExpectedCategorySlug]);

  const productPrice = React.useMemo(() => {
    if (!product) return 0;
    // If taste is selected, get price from selected taste
    if (selectedTaste !== null && product.tastes && product.tastes[selectedTaste]) {
      return product.tastes[selectedTaste].price || product.tastes[selectedTaste] || 0;
    }
    return product.price || 0;
  }, [product, selectedTaste]);

  useEffect(() => {
    const run = async () => {
      if (!product) {
        setMatchedDiscount(null);
        return;
      }
      try {
        const discounts = await getActiveDiscounts();
        const best = getBestDiscountForProduct(product, discounts);
        setMatchedDiscount(best || null);
      } catch (e) {
        console.error('Failed to fetch discounts for product', e);
        setMatchedDiscount(null);
      }
    };
    run();
  }, [product?.id, product?.category, product?.categoryId, (product?.categories || []).join('|')]);

  const pricing = React.useMemo(() => {
    return computeDiscountedPrice(productPrice, matchedDiscount);
  }, [productPrice, matchedDiscount]);

  const discountBadgeText = React.useMemo(() => {
    return formatDiscountBadge(matchedDiscount);
  }, [matchedDiscount]);

  const wished = isInWishlist ? isInWishlist(product) : false;

  if (loading) return <LoadingScreen />;

  if (!product) {
    return (
      <div className="container" style={{ padding: '150px 20px', textAlign: 'center' }}>
        <h2>Product Not Found</h2>
        <Link to="/shop" className="btn">Back to Shop</Link>
      </div>
    );
  }

  const productName = (() => {
    if (language === 'ar' && product.nameAr) return product.nameAr;
    if (language === 'en' && product.nameEn) return product.nameEn;
    
    // Fallback
    let fullName = '';
    if (typeof product.name === 'string') {
      fullName = product.name;
    } else if (typeof product.name === 'object' && product.name !== null) {
      fullName = product.name.en || product.name.ar || product.nameEn || 'Unnamed Product';
    }
    return getLanguageText(fullName, language);
  })();
  
  const productImages = product.imageUrls && product.imageUrls.length > 0 
    ? product.imageUrls 
    : (product.images && product.images.length > 0 ? product.images : (product.imageUrl ? [product.imageUrl] : []));

  const currentImage = productImages[selectedImageIndex] || "https://placehold.co/600x600?text=No+Image";

  const handlePrevImage = () => {
    if (!productImages || productImages.length <= 1) return;
    setSelectedImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const handleNextImage = () => {
    if (!productImages || productImages.length <= 1) return;
    setSelectedImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const productStock = (() => {
    if (selectedTaste !== null && product.tastes && product.tastes[selectedTaste]) {
      return product.tastes[selectedTaste].stock || 0;
    }
    return product.stock || 0;
  })();
  
  const productDescription = (() => {
    if (language === 'ar' && product.descriptionAr) return product.descriptionAr;
    if (language === 'en' && product.descriptionEn) return product.descriptionEn;

    let fullDesc = '';
    if (typeof product.description === 'string') {
      fullDesc = product.description;
    } else if (typeof product.description === 'object' && product.description !== null) {
      fullDesc = product.description.en || product.description.ar || 'No description available.';
    } else {
      fullDesc = 'No description available.';
    }
    return getLanguageText(fullDesc, language);
  })();

  const handleQuantityChange = (change) => {
    const newQty = quantity + change;
    if (newQty >= 1 && newQty <= productStock) setQuantity(newQty);
  };

  const handleAddToCart = () => {
    // Check if taste is required but not selected
    if (product.tastes && product.tastes.length > 0 && selectedTaste === null) {
      alert('Please select a taste before adding to cart');
      return;
    }
    
    const selectedTasteObj = selectedTaste !== null ? product.tastes[selectedTaste] : null;
    
    const productToAdd = {
      ...product,
      baseProductId: product.id,
      basePrice: productPrice,
      selectedPrice: productPrice,
      price: productPrice,
      selectedTaste: selectedTasteObj,
      // Create unique ID for product+taste combination
      id: selectedTasteObj ? `${product.id}_${selectedTasteObj.name || selectedTaste}` : product.id,
      image: currentImage // Use currently selected image
    };
    addToCart(productToAdd, quantity);
  };

  const handleBuyNow = () => {
    // Check if taste is required but not selected
    if (product.tastes && product.tastes.length > 0 && selectedTaste === null) {
      alert('Please select a taste before buying');
      return;
    }
    
    const selectedTasteObj = selectedTaste !== null ? product.tastes[selectedTaste] : null;
    
    // Create a direct checkout item (don't add to cart)
    const directCheckoutItem = {
      ...product,
      baseProductId: product.id,
      basePrice: productPrice,
      selectedPrice: productPrice,
      price: productPrice,
      selectedTaste: selectedTasteObj,
      // Create unique ID for product+taste combination
      id: selectedTasteObj ? `${product.id}_${selectedTasteObj.name || selectedTaste}` : product.id,
      image: currentImage,
      quantity: quantity
    };
    
    // Navigate to checkout with this single item
    navigate('/checkout', { 
      state: { 
        directCheckoutItem: directCheckoutItem,
        isDirectCheckout: true
      } 
    });
  };

  const isOutOfStock = productStock <= 0;

  const seoTitle = `${productName} | VetCare Store`;
  const seoDescription = (productDescription || '').toString().replace(/\s+/g, ' ').trim().slice(0, 180);
  const seoImage = currentImage;

  const canonicalCategorySlug = getExpectedCategorySlug(product, categories);
  const seoCanonical = (typeof window !== 'undefined')
    ? `${window.location.origin}/products/${canonicalCategorySlug || categoryName}/${productSlug}`
    : '';

  const origin = (typeof window !== 'undefined') ? window.location.origin : '';
  const breadcrumbStructuredData = origin ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${origin}/`
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Shop',
        item: `${origin}/shop`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: resolveCategoryLabel(categoryName) || categoryName,
        item: `${origin}/shop/${encodeURIComponent(canonicalCategorySlug || categoryName)}`
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: productName,
        item: seoCanonical || (typeof window !== 'undefined' ? window.location.href : '')
      }
    ]
  } : null;

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: productName,
    image: [seoImage].filter(Boolean),
    description: seoDescription,
    sku: product?.id,
    brand: {
      "@type": "Brand",
      name: "VetCare Store"
    },
    offers: {
      "@type": "Offer",
      priceCurrency: "EGP",
        price: Number((pricing?.finalPrice ?? productPrice) || 0),
      availability: isOutOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      url: seoCanonical || (typeof window !== 'undefined' ? window.location.href : '')
    }
  };

  return (
    <div className="product-details-page" style={{ paddingTop: '40px', paddingBottom: '80px' }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        type="product"
        canonical={seoCanonical}
        locale={language === 'ar' ? 'ar_EG' : 'en_EG'}
        htmlLang={language === 'ar' ? 'ar' : 'en'}
        htmlDir={language === 'ar' ? 'rtl' : 'ltr'}
        structuredData={breadcrumbStructuredData ? [breadcrumbStructuredData, productStructuredData] : productStructuredData}
      />
      <div className="container">

        <div className="product-grid-layout">
          {/* Left Column: Image */}
          <div className="product-image-section">
            <div className="main-image-container" style={{ 
              border: '1px solid #eee', 
              borderRadius: '10px', 
              overflow: 'hidden',
              position: 'relative',
              marginBottom: '10px',
              maxHeight: '500px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff'
            }}>
              <img 
                src={currentImage} 
                alt={productName} 
                style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain', display: 'block' }} 
              />

              {productImages.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous image"
                    onClick={handlePrevImage}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '12px',
                      transform: 'translateY(-50%)',
                      background: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                      cursor: 'pointer',
                      zIndex: 2
                    }}
                  >
                    <ion-icon name="chevron-back-outline" style={{ fontSize: '22px' }}></ion-icon>
                  </button>

                  <button
                    type="button"
                    aria-label="Next image"
                    onClick={handleNextImage}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '12px',
                      transform: 'translateY(-50%)',
                      background: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                      cursor: 'pointer',
                      zIndex: 2
                    }}
                  >
                    <ion-icon name="chevron-forward-outline" style={{ fontSize: '22px' }}></ion-icon>
                  </button>
                </>
              )}

              <button
                className="wishlist-btn"
                type="button"
                aria-label="Wishlist"
                onClick={() => {
                  if (!product || !toggleWishlist) return;
                  toggleWishlist({ ...product, baseProductId: product.id });
                }}
                style={{
                position: 'absolute',
                top: '15px',
                left: '15px',
                background: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                cursor: 'pointer'
              }}>
                <ion-icon name={wished ? 'heart' : 'heart-outline'} style={{ fontSize: '20px' }}></ion-icon>
              </button>
            </div>
            
            {/* Thumbnails */}
            {productImages.length > 1 && (
              <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '5px' }}>
                {productImages.map((img, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => setSelectedImageIndex(idx)}
                    style={{
                      width: '80px', 
                      height: '80px', 
                      border: selectedImageIndex === idx ? '2px solid #1B92A8' : '1px solid #eee',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  >
                    <img src={img} alt={`Thumbnail ${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column: Details */}
          <div className="product-info-section" style={{ padding: '0 20px' }}>
            
            {/* Category/Brand Label */}
            <div style={{ 
              textTransform: 'uppercase', 
              fontSize: '0.85rem', 
              fontWeight: '700', 
              color: '#999', 
              marginBottom: '10px',
              letterSpacing: '1px',
              textAlign: language === 'ar' ? 'right' : 'left'
            }}>
              {product.categories && product.categories.length > 0
                ? resolveCategoryLabel(product.categories[0])
                : (product.category ? resolveCategoryLabel(product.category) : 'VETCARE STORE')}
            </div>

            {/* Product Title */}
            <h1 style={{ 
              fontSize: '2.5rem', 
              fontWeight: '800', 
              color: '#222', 
              marginBottom: '15px',
              lineHeight: '1.2',
              fontFamily: '"Nunito Sans", sans-serif',
              textAlign: language === 'ar' ? 'right' : 'left'
            }}>
              {productName}
            </h1>

            {/* Price & Stock Status */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '20px', 
              marginBottom: '25px',
              flexDirection: language === 'ar' ? 'row-reverse' : 'row',
              justifyContent: language === 'ar' ? 'flex-start' : 'flex-start'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'baseline',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                {matchedDiscount && pricing.finalPrice < pricing.basePrice ? (
                  <>
                    <span style={{
                      fontSize: '1.35rem',
                      fontWeight: '700',
                      color: '#6b7280',
                      textDecoration: 'line-through',
                      fontFamily: '"Nunito Sans", sans-serif'
                    }}>
                      {pricing.basePrice}.00 {language === 'ar' ? 'ج.م' : 'EGP'}
                    </span>
                    <span style={{
                      fontSize: '2rem',
                      fontWeight: '800',
                      color: 'var(--portland-orange)',
                      fontFamily: '"Nunito Sans", sans-serif'
                    }}>
                      {pricing.finalPrice}.00 {language === 'ar' ? 'ج.م' : 'EGP'}
                    </span>
                    <span style={{
                      background: '#ff4d4f',
                      color: '#fff',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      lineHeight: 1
                    }}>
                      {discountBadgeText || 'OFF'}
                    </span>
                  </>
                ) : (
                  <span style={{
                    fontSize: '2rem',
                    fontWeight: '800',
                    color: 'var(--portland-orange)',
                    fontFamily: '"Nunito Sans", sans-serif'
                  }}>
                    {productPrice}.00 {language === 'ar' ? 'ج.م' : 'EGP'}
                  </span>
                )}
              </div>
              <div style={{ 
                padding: '5px 12px', 
                borderRadius: '20px', 
                backgroundColor: isOutOfStock ? '#fee2e2' : '#dcfce7', 
                color: isOutOfStock ? '#dc2626' : '#16a34a',
                fontSize: '0.85rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {isOutOfStock 
                  ? (language === 'ar' ? 'نفذت الكمية' : 'Out of Stock') 
                  : (language === 'ar' ? 'متوفر' : 'In Stock')}
              </div>
            </div>

            {/* Short Description */}
            <div style={{ 
              marginBottom: '30px', 
              color: '#555', 
              lineHeight: '1.7', 
              fontSize: '1.05rem',
              textAlign: language === 'ar' ? 'right' : 'left'
            }}>
              {productDescription}
            </div>

            <div style={{ height: '1px', backgroundColor: '#eee', marginBottom: '30px' }}></div>

            {/* Options Container */}
            <div style={{ marginBottom: '30px' }}>
              {/* Size Selection */}
              {product.size && (
                <div style={{ marginBottom: '20px', textAlign: language === 'ar' ? 'right' : 'left' }}>
                  <span style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '0.95rem', color: '#333', textTransform: 'uppercase' }}>
                    {language === 'ar' ? 'الحجم' : 'Size'}
                  </span>
                  <div style={{ 
                    display: 'inline-block', 
                    padding: '10px 20px', 
                    border: '2px solid #1B92A8', 
                    borderRadius: '8px', 
                    color: '#1B92A8', 
                    fontWeight: '700',
                    backgroundColor: '#f0fdff'
                  }}>
                    {product.size} {product.sizeUnit}
                  </div>
                </div>
              )}

              {/* Taste Selection */}
              {product.tastes && product.tastes.length > 0 && (
                <div style={{ marginBottom: '20px', textAlign: language === 'ar' ? 'right' : 'left' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '700', fontSize: '0.95rem', color: '#333', textTransform: 'uppercase' }}>
                    {language === 'ar' ? 'اختر الطعم' : 'Select Taste'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select 
                      value={selectedTaste !== null ? selectedTaste : ''}
                      onChange={(e) => setSelectedTaste(Number(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '15px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        backgroundColor: '#fff',
                        color: '#333',
                        cursor: 'pointer',
                        appearance: 'none',
                        outline: 'none',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.02)',
                        direction: language === 'ar' ? 'rtl' : 'ltr'
                      }}
                    >
                      <option value="" disabled>{language === 'ar' ? 'اختر...' : 'Choose a taste...'}</option>
                      {product.tastes.map((taste, index) => (
                        <option key={index} value={index}>
                          {taste.name} {taste.stock <= 0 ? `(${language === 'ar' ? 'نفذت الكمية' : 'Out of Stock'})` : ''}
                        </option>
                      ))}
                    </select>
                    <div style={{ 
                      position: 'absolute', 
                      top: '50%', 
                      [language === 'ar' ? 'left' : 'right']: '15px', 
                      transform: 'translateY(-50%)', 
                      pointerEvents: 'none',
                      color: '#666'
                    }}>
                      <ion-icon name="chevron-down-outline"></ion-icon>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions Container */}
            <div style={{ 
              display: 'flex', 
              gap: '15px', 
              marginBottom: '30px', 
              flexWrap: 'wrap',
              flexDirection: language === 'ar' ? 'row-reverse' : 'row'
            }}>
              {/* Quantity */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '8px',
                height: '50px',
                backgroundColor: '#fff'
              }}>
                <button onClick={() => handleQuantityChange(-1)} style={{ width: '40px', height: '100%', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#666' }}>−</button>
                <span style={{ width: '40px', textAlign: 'center', fontWeight: '600', fontSize: '1.1rem' }}>{quantity}</span>
                <button onClick={() => handleQuantityChange(1)} style={{ width: '40px', height: '100%', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#666' }}>+</button>
              </div>

              {/* Add to Cart */}
              <button 
                onClick={handleAddToCart}
                disabled={isOutOfStock}
                style={{
                  flex: '1',
                  height: '50px',
                  backgroundColor: isOutOfStock ? '#ccc' : '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'background 0.3s'
                }}
                onMouseOver={(e) => !isOutOfStock && (e.currentTarget.style.backgroundColor = '#000')}
                onMouseOut={(e) => !isOutOfStock && (e.currentTarget.style.backgroundColor = '#333')}
              >
                <ion-icon name="cart-outline" style={{ fontSize: '1.2rem' }}></ion-icon>
                {language === 'ar' ? 'أضف إلى السلة' : 'Add to Cart'}
              </button>

              {/* Buy Now */}
              <button 
                onClick={handleBuyNow}
                disabled={isOutOfStock}
                style={{
                  flex: '1',
                  height: '50px',
                  backgroundColor: isOutOfStock ? '#ccc' : 'var(--portland-orange)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'background 0.3s'
                }}
                onMouseOver={(e) => !isOutOfStock && (e.currentTarget.style.backgroundColor = '#e05a00')}
                onMouseOut={(e) => !isOutOfStock && (e.currentTarget.style.backgroundColor = 'var(--portland-orange)')}
              >
                <ion-icon name="flash-outline" style={{ fontSize: '1.2rem' }}></ion-icon>
                {language === 'ar' ? 'شراء الآن' : 'Buy Now'}
              </button>
            </div>

            {/* Share & Meta */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              borderTop: '1px solid #eee', 
              paddingTop: '20px',
              flexDirection: language === 'ar' ? 'row-reverse' : 'row'
            }}>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#333' }}>{language === 'ar' ? 'مشاركة:' : 'Share:'}</span>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button style={{ width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #eee', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b5998', cursor: 'pointer' }}><ion-icon name="logo-facebook"></ion-icon></button>
                  <button style={{ width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #eee', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1da1f2', cursor: 'pointer' }}><ion-icon name="logo-twitter"></ion-icon></button>
                  <button style={{ width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #eee', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#25d366', cursor: 'pointer' }}><ion-icon name="logo-whatsapp"></ion-icon></button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="product-tabs" style={{ marginTop: '80px' }}>
          <div className="tab-headers" style={{ 
            display: 'flex', 
            gap: '30px', 
            borderBottom: '1px solid #eee',
            marginBottom: '30px',
            flexDirection: language === 'ar' ? 'row-reverse' : 'row'
          }}>
            <button 
              className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => setActiveTab('description')}
              style={{
                padding: '15px 0',
                background: 'none',
                border: 'none',
                fontSize: '1.1rem',
                fontWeight: activeTab === 'description' ? 'bold' : 'normal',
                color: activeTab === 'description' ? 'var(--portland-orange)' : '#666',
                borderBottom: activeTab === 'description' ? '2px solid var(--portland-orange)' : 'none',
                cursor: 'pointer'
              }}
            >
              {language === 'ar' ? 'الوصف' : 'Description'}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'tags' ? 'active' : ''}`}
              onClick={() => setActiveTab('tags')}
              style={{
                padding: '15px 0',
                background: 'none',
                border: 'none',
                fontSize: '1.1rem',
                fontWeight: activeTab === 'tags' ? 'bold' : 'normal',
                color: activeTab === 'tags' ? 'var(--portland-orange)' : '#666',
                borderBottom: activeTab === 'tags' ? '2px solid var(--portland-orange)' : 'none',
                cursor: 'pointer'
              }}
            >
              {language === 'ar' ? 'الوسوم' : 'Tags'}
            </button>
            {relatedProducts.length > 0 && (
              <button 
                className={`tab-btn ${activeTab === 'related' ? 'active' : ''}`}
                onClick={() => setActiveTab('related')}
                style={{
                  padding: '15px 0',
                  background: 'none',
                  border: 'none',
                  fontSize: '1.1rem',
                  fontWeight: activeTab === 'related' ? 'bold' : 'normal',
                  color: activeTab === 'related' ? 'var(--portland-orange)' : '#666',
                  borderBottom: activeTab === 'related' ? '2px solid var(--portland-orange)' : 'none',
                  cursor: 'pointer'
                }}
              >
                {language === 'ar' ? 'قد يعجبك أيضاً' : 'You May Also Like'}
              </button>
            )}
          </div>

          <div className="tab-content">
            {activeTab === 'tags' && (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: language === 'ar' ? 'flex-end' : 'flex-start' }}>
                {product.categories && product.categories.map((cat, idx) => (
                  <span key={idx} style={{ 
                    background: '#f0f0f0', 
                    padding: '5px 15px', 
                    borderRadius: '20px', 
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    {resolveCategoryLabel(cat)}
                  </span>
                ))}
                {!product.categories && product.category && (
                  <span style={{ 
                    background: '#f0f0f0', 
                    padding: '5px 15px', 
                    borderRadius: '20px', 
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    {resolveCategoryLabel(product.category)}
                  </span>
                )}
              </div>
            )}
            {activeTab === 'description' && (
               <div style={{ 
                 lineHeight: '1.8', 
                 color: '#555', 
                 textAlign: language === 'ar' ? 'right' : 'left',
                 whiteSpace: 'pre-line'
               }}>
                 {productDescription}
               </div>
            )}
            {activeTab === 'related' && (
              <div className="related-products-grid">
                {relatedProducts.map(p => (
                  <ProductCard key={p.id} product={p} categories={categories} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .related-title {
          font-family: 'Nunito Sans', sans-serif;
          font-size: 2.2rem;
          font-weight: 800;
          color: #202223;
          margin-bottom: 40px;
          position: relative;
          padding-bottom: 15px;
          letter-spacing: -0.5px;
        }

        .related-title::after {
          content: "";
          position: absolute;
          bottom: 0;
          width: 80px;
          height: 4px;
          background-color: #0d918b;
          border-radius: 2px;
        }

        .related-title:not(.rtl) {
          text-align: left;
        }
        
        .related-title:not(.rtl)::after {
          left: 0;
        }

        .related-title.rtl {
          text-align: right;
        }

        .related-title.rtl::after {
          right: 0;
        }

        .product-grid-layout {
          display: grid;
          grid-template-columns: 0.8fr 1.2fr;
          gap: 50px;
          align-items: start;
        }
        
        .related-products-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }

        @media (max-width: 1200px) {
          .related-products-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 992px) {
          .related-products-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .product-grid-layout {
            grid-template-columns: 1fr;
            gap: 30px;
          }
        }

        @media (max-width: 576px) {
          .related-products-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
        }
      `}</style>
    </div>
  );
}
